import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Eye,
  CheckCircle,
  FileEdit,
  AlertCircle,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient, SSWContent } from '@parentpath/supabase-client';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { useAdminLocale } from '../../hooks/useAdminLocale';
import { LocaleSelector } from '../../components/LocaleSelector';

const supabase = getSupabaseAdminClient();

type OverviewSection = {
  title: string | null;
  content: string;
};

type SSWContentFormData = {
  title: string;
  baby_development: string[];
  baby_size_cm: number | null;
  baby_weight_grams: number | null;
  baby_comparison: string;
  mother_body: string[];
  tips: string[];
  partner_tips: string;
  overview_sections: OverviewSection[];
  is_published: boolean;
};

export function SSWContentEditor() {
  const { week } = useParams<{ week: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const weekNumber = parseInt(week || '1', 10);
  const { contentLocale } = useAdminLocale();

  const [formData, setFormData] = useState<SSWContentFormData>({
    title: `Schwangerschaftswoche ${weekNumber}`,
    baby_development: [''],
    baby_size_cm: null,
    baby_weight_grams: null,
    baby_comparison: '',
    mother_body: [''],
    tips: [''],
    partner_tips: '',
    overview_sections: [{ title: null, content: '' }],
    is_published: false,
  });

  // Fetch existing content for selected locale
  const { data: existingContent, isLoading } = useQuery({
    queryKey: ['ssw-content', weekNumber, contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ssw_content')
        .select('*')
        .eq('week_number', weekNumber)
        .eq('locale', contentLocale)
        .maybeSingle();

      if (error) throw error;
      return data as SSWContent | null;
    },
  });

  // Fetch German reference content (when editing non-DE locale)
  const { data: deReferenceContent } = useQuery({
    queryKey: ['ssw-content', weekNumber, 'de'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ssw_content')
        .select('*')
        .eq('week_number', weekNumber)
        .eq('locale', 'de')
        .maybeSingle();

      if (error) throw error;
      return data as SSWContent | null;
    },
    enabled: contentLocale !== 'de',
  });

  // Helper to convert string or array to array format
  const toArrayFormat = (value: string | string[] | null | undefined): string[] => {
    if (!value) return [''];
    if (Array.isArray(value)) return value.length ? value : [''];
    // Try to parse as JSON array first (handles stored JSON strings)
    if (value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          // Check if it's a nested JSON string (double-encoded)
          if (parsed.length === 1 && typeof parsed[0] === 'string' && parsed[0].startsWith('[')) {
            try {
              const innerParsed = JSON.parse(parsed[0]);
              if (Array.isArray(innerParsed)) {
                return innerParsed.length ? innerParsed : [''];
              }
            } catch {
              // Not double-encoded, use outer array
            }
          }
          return parsed.length ? parsed : [''];
        }
      } catch {
        // Not valid JSON, fall through to newline split
      }
    }
    // Old string format: split by newlines
    const lines = value.split('\n').filter((line) => line.trim() !== '');
    return lines.length ? lines : [''];
  };

  // Parse overview_sections from DB (JSONB) into typed array
  const parseOverviewSections = (raw: unknown, partnerTipsFallback: string | null): OverviewSection[] => {
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((s: { title?: string | null; content?: string }) => ({
        title: s.title ?? null,
        content: s.content || '',
      }));
    }
    // Fallback: migrate from partner_tips
    if (partnerTipsFallback && partnerTipsFallback !== '<p></p>') {
      return [{ title: null, content: partnerTipsFallback }];
    }
    return [{ title: null, content: '' }];
  };

  // Load existing data into form
  useEffect(() => {
    if (existingContent) {
      const sections = parseOverviewSections(
        (existingContent as Record<string, unknown>).overview_sections,
        existingContent.partner_tips,
      );
      setFormData({
        title: existingContent.title || `Schwangerschaftswoche ${weekNumber}`,
        baby_development: toArrayFormat(existingContent.baby_development),
        baby_size_cm: existingContent.baby_size_cm,
        baby_weight_grams: existingContent.baby_weight_grams,
        baby_comparison: existingContent.baby_comparison || '',
        mother_body: toArrayFormat(existingContent.mother_body),
        tips: existingContent.tips?.length ? existingContent.tips : [''],
        partner_tips: existingContent.partner_tips || '',
        overview_sections: sections,
        is_published: existingContent.is_published || false,
      });
    }
  }, [existingContent, weekNumber]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (publish: boolean) => {
      // Filter out empty sections
      const cleanedSections = formData.overview_sections
        .filter((s) => s.content && s.content !== '<p></p>');

      // Keep partner_tips in sync (concatenated HTML for backward compat)
      const concatenatedHtml = cleanedSections.map((s) => s.content).join('');

      const dataToSave = {
        week_number: weekNumber,
        title: formData.title,
        baby_development: formData.baby_development.filter((t) => t.trim() !== ''),
        baby_size_cm: formData.baby_size_cm,
        baby_weight_grams: formData.baby_weight_grams,
        baby_comparison: formData.baby_comparison,
        mother_body: formData.mother_body.filter((t) => t.trim() !== ''),
        tips: formData.tips.filter((t) => t.trim() !== ''),
        partner_tips: concatenatedHtml || '',
        overview_sections: cleanedSections,
        locale: contentLocale,
        is_published: publish,
      };

      if (existingContent) {
        const { error } = await supabase
          .from('ssw_content')
          .update(dataToSave as never)
          .eq('id', existingContent.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('ssw_content')
          .insert(dataToSave as never);

        if (error) throw error;
      }
    },
    onSuccess: (_, publish) => {
      toast.success(publish ? 'SSW-Inhalt veröffentlicht!' : 'Entwurf gespeichert!');
      queryClient.invalidateQueries({ queryKey: ['ssw-content'] });
      setFormData((prev) => ({ ...prev, is_published: publish }));
    },
    onError: (error) => {
      console.error(error);
      toast.error('Fehler beim Speichern');
    },
  });

  // Generic handlers for array fields
  const handleArrayChange = (field: 'tips' | 'baby_development' | 'mother_body', index: number, value: string) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayItem = (field: 'tips' | 'baby_development' | 'mother_body') => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayItem = (field: 'tips' | 'baby_development' | 'mother_body', index: number) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray.length ? newArray : [''] });
  };

  // Legacy handlers for tips (for backwards compatibility)
  const handleTipChange = (index: number, value: string) => handleArrayChange('tips', index, value);
  const addTip = () => addArrayItem('tips');
  const removeTip = (index: number) => removeArrayItem('tips', index);

  // Overview section handlers
  const updateSectionContent = (index: number, html: string) => {
    const newSections = [...formData.overview_sections];
    newSections[index] = { ...newSections[index], content: html };
    setFormData({ ...formData, overview_sections: newSections });
  };

  const updateSectionTitle = (index: number, title: string) => {
    const newSections = [...formData.overview_sections];
    newSections[index] = { ...newSections[index], title: title || null };
    setFormData({ ...formData, overview_sections: newSections });
  };

  const addSection = () => {
    setFormData({
      ...formData,
      overview_sections: [...formData.overview_sections, { title: null, content: '' }],
    });
  };

  const removeSection = (index: number) => {
    const newSections = formData.overview_sections.filter((_, i) => i !== index);
    setFormData({ ...formData, overview_sections: newSections });
  };

  const moveSectionUp = (index: number) => {
    if (index <= 1) return; // Can't move first section or already at top of movable range
    const newSections = [...formData.overview_sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    setFormData({ ...formData, overview_sections: newSections });
  };

  const moveSectionDown = (index: number) => {
    if (index === 0 || index >= formData.overview_sections.length - 1) return;
    const newSections = [...formData.overview_sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    setFormData({ ...formData, overview_sections: newSections });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Lädt...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/content/ssw')}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-slate-900">
                SSW {weekNumber} bearbeiten
              </h1>
              <LocaleSelector />
            </div>
            <div className="flex items-center gap-2 mt-1">
              {formData.is_published ? (
                <span className="flex items-center gap-1 text-emerald-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Veröffentlicht
                </span>
              ) : existingContent ? (
                <span className="flex items-center gap-1 text-amber-600 text-sm">
                  <FileEdit className="w-4 h-4" />
                  Entwurf
                </span>
              ) : (
                <span className="flex items-center gap-1 text-slate-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Neu
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => saveMutation.mutate(false)}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Als Entwurf speichern
          </button>
          <button
            onClick={() => saveMutation.mutate(true)}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Veröffentlichen
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - All Fields */}
        <div className="space-y-6">
          {/* Title */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Titel
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              placeholder="z.B. Schwangerschaftswoche 12"
            />
          </div>

          {/* Baby Size */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Baby-Größe</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Größe (cm)
                </label>
                <input
                  type="number"
                  value={formData.baby_size_cm || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      baby_size_cm: e.target.value ? parseFloat(e.target.value) : null,
                    })
                  }
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="z.B. 5.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Gewicht (g)
                </label>
                <input
                  type="number"
                  value={formData.baby_weight_grams || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      baby_weight_grams: e.target.value ? parseFloat(e.target.value) : null,
                    })
                  }
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="z.B. 50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Größenvergleich
                </label>
                <input
                  type="text"
                  value={formData.baby_comparison}
                  onChange={(e) => setFormData({ ...formData, baby_comparison: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  placeholder="z.B. Himbeere"
                />
              </div>
            </div>
          </div>

          {/* Baby Development */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Baby-Entwicklung
            </label>
            <div className="space-y-3">
              {formData.baby_development.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleArrayChange('baby_development', index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    placeholder={`Entwicklung ${index + 1}`}
                  />
                  <button
                    onClick={() => removeArrayItem('baby_development', index)}
                    className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => addArrayItem('baby_development')}
                className="w-full px-4 py-2 border-2 border-dashed border-slate-300 text-slate-500 rounded-lg hover:bg-slate-50"
              >
                + Zeile hinzufügen
              </button>
            </div>
          </div>

          {/* Mother's Body */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Körperliche Veränderungen (Mutter)
            </label>
            <div className="space-y-3">
              {formData.mother_body.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleArrayChange('mother_body', index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    placeholder={`Veränderung ${index + 1}`}
                  />
                  <button
                    onClick={() => removeArrayItem('mother_body', index)}
                    className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => addArrayItem('mother_body')}
                className="w-full px-4 py-2 border-2 border-dashed border-slate-300 text-slate-500 rounded-lg hover:bg-slate-50"
              >
                + Zeile hinzufügen
              </button>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tipps für die Woche
            </label>
            <div className="space-y-3">
              {formData.tips.map((tip, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={tip}
                    onChange={(e) => handleTipChange(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    placeholder={`Tipp ${index + 1}`}
                  />
                  <button
                    onClick={() => removeTip(index)}
                    className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={addTip}
                className="w-full px-4 py-2 border-2 border-dashed border-slate-300 text-slate-500 rounded-lg hover:bg-slate-50"
              >
                + Tipp hinzufügen
              </button>
            </div>
          </div>

          {/* Wochenübersicht - Multi-Section Editor */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Wochenübersicht</h3>
            <div className="space-y-4">
              {formData.overview_sections.map((section, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 ${index === 0 ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200'}`}
                >
                  {index === 0 ? (
                    <p className="text-xs text-slate-500 mb-2">Einleitungstext (wird direkt unter der Überschrift angezeigt)</p>
                  ) : (
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={section.title || ''}
                        onChange={(e) => updateSectionTitle(index, e.target.value)}
                        className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                        placeholder="Überschrift (optional)"
                      />
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => moveSectionUp(index)}
                          disabled={index <= 1}
                          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          title="Nach oben"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveSectionDown(index)}
                          disabled={index >= formData.overview_sections.length - 1}
                          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          title="Nach unten"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeSection(index)}
                          className="p-1 text-red-400 hover:text-red-600"
                          title="Abschnitt entfernen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <RichTextEditor
                    value={section.content}
                    onChange={(html) => updateSectionContent(index, html)}
                    placeholder={index === 0 ? 'Einleitungstext zur Wochenübersicht...' : 'Inhalt dieses Abschnitts...'}
                    minHeight="120px"
                  />
                </div>
              ))}
              <button
                onClick={addSection}
                className="w-full px-4 py-2 border-2 border-dashed border-slate-300 text-slate-500 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Abschnitt hinzufügen
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Preview & Reference */}
        <div className="space-y-6 lg:sticky lg:top-4 lg:self-start">
          {/* German Reference - shown when editing non-DE locale */}
          {contentLocale !== 'de' && deReferenceContent && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-800 mb-3">{'\u{1F1E9}\u{1F1EA}'} Deutsche Vorlage</h3>
              <div className="space-y-2 text-sm text-blue-900">
                <div><strong>Titel:</strong> {deReferenceContent.title}</div>
                {deReferenceContent.baby_comparison && (
                  <div><strong>Vergleich:</strong> {deReferenceContent.baby_comparison}</div>
                )}
              </div>
            </div>
          )}

          <div className="bg-slate-800 rounded-lg shadow-sm p-6 text-white">
            <h3 className="text-lg font-semibold text-slate-300 mb-6">Vorschau</h3>

            {/* SSW Header */}
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-teal-400">{weekNumber}</div>
              <div className="text-sm text-slate-400">SSW</div>
              <div className="text-lg font-semibold mt-2">
                {formData.title || `Schwangerschaftswoche ${weekNumber}`}
              </div>
            </div>

            {/* Baby Size Preview */}
            {(formData.baby_size_cm || formData.baby_weight_grams || formData.baby_comparison) && (
              <div className="mb-6 space-y-2">
                {formData.baby_comparison && (
                  <div className="text-sm text-slate-300">
                    Größenvergleich: <span className="text-white font-medium">{formData.baby_comparison}</span>
                  </div>
                )}
                {formData.baby_size_cm && (
                  <div className="text-sm text-slate-300">
                    {formData.baby_size_cm} cm
                    {formData.baby_weight_grams && ` · ${formData.baby_weight_grams} g`}
                  </div>
                )}
              </div>
            )}

            {/* Baby Development Preview */}
            {formData.baby_development.some((d) => d.trim()) && (
              <div className="mb-6">
                <h4 className="text-base font-semibold mb-3">Entwicklung</h4>
                <div className="space-y-2">
                  {formData.baby_development
                    .filter((d) => d.trim())
                    .map((item, index) => (
                      <div key={index} className="bg-white/10 rounded-xl p-3 flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Mother's Body Preview */}
            {formData.mother_body.some((m) => m.trim()) && (
              <div className="mb-6">
                <h4 className="text-base font-semibold mb-1">Dein Körper</h4>
                <p className="text-slate-400 text-xs italic mb-3">Mögliche Veränderungen</p>
                <div className="space-y-2">
                  {formData.mother_body
                    .filter((m) => m.trim())
                    .map((item, index) => (
                      <div key={index} className="bg-white/10 rounded-xl p-3 flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Tips Preview */}
            {formData.tips.some((t) => t.trim()) && (
              <div className="mb-6">
                <h4 className="text-base font-semibold mb-3">Tipps für die Woche</h4>
                <div className="space-y-2">
                  {formData.tips
                    .filter((t) => t.trim())
                    .map((tip, index) => (
                      <div key={index} className="bg-white/10 rounded-xl p-3 flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{tip}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Wochenübersicht Preview (Multi-Section) */}
            {formData.overview_sections.some((s) => s.content && s.content !== '<p></p>') && (
              <div className="mb-6">
                <h4 className="text-base font-semibold mb-3">Wochenübersicht</h4>
                <div className="space-y-3">
                  {formData.overview_sections
                    .filter((s) => s.content && s.content !== '<p></p>')
                    .map((section, index) => (
                      <div key={index}>
                        {section.title && index > 0 && (
                          <h5 className="text-sm font-semibold text-white/90 mb-2">{section.title}</h5>
                        )}
                        <div className="bg-white/10 rounded-xl p-4">
                          <div
                            className="prose prose-sm prose-invert max-w-none prose-p:text-white/80 prose-p:leading-relaxed prose-p:my-1 prose-headings:text-white prose-strong:text-white prose-a:text-teal-400 prose-li:text-white/80"
                            dangerouslySetInnerHTML={{ __html: section.content }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
